import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

import ErrorMessage from "@components/form/ErrorMessage";
import { formatDate, formatDateInput } from "@utils/formatDate";
import {
  commitCell,
  type InlineEditableField,
} from "@utils/bulkMeterUpload/inlineRowEdit";
import type { MeterUploadRowData } from "@utils/bulkMeterUpload/buildTrialRunRow";

type InlineEditCellProps = {
  field: InlineEditableField;
  rowData: MeterUploadRowData;
  // Non-ledgered rows are editable; ledgered (Success) rows render read-only text.
  editable: boolean;
  // Commits the validated, patched rowData. The caller decides local-vs-persist
  // (useBulkMeterUploadFlow.editRow: a saved draft PATCHes the field, a fresh
  // upload mutates local state only).
  onCommit: (rowData: MeterUploadRowData) => void | Promise<void>;
};

const inputType: Record<InlineEditableField, string> = {
  reading: "number",
  readAt: "date",
  serialNo: "text",
  group: "text",
  seq: "number",
};

const labelKey: Record<InlineEditableField, string> = {
  reading: "bulk_meter_upload.table.meter_read",
  readAt: "bulk_meter_upload.table.date",
  serialNo: "bulk_meter_upload.table.meter_serial_no",
  group: "bulk_meter_upload.table.group",
  seq: "bulk_meter_upload.table.seq",
};

// The value used to seed the <input> while editing. `readAt` is normalised to the
// yyyy-mm-dd a date input expects.
const seedValue = (field: InlineEditableField, rowData: MeterUploadRowData): string => {
  switch (field) {
    case "reading":
      return rowData.reading == null ? "" : String(rowData.reading);
    case "readAt":
      return rowData.readAt ? formatDateInput(new Date(rowData.readAt)) : "";
    case "serialNo":
      return rowData.serialNo ?? "";
    case "group":
      return rowData.group ?? "";
    case "seq":
      return rowData.seq == null ? "" : String(rowData.seq);
  }
};

// The text shown in a read-only (ledgered) cell. `readAt` renders as dd/MM/yyyy to
// match the rest of the table; the editable input uses the yyyy-mm-dd seed instead.
const displayText = (field: InlineEditableField, rowData: MeterUploadRowData): string => {
  if (field === "readAt") {
    return rowData.readAt ? formatDate(new Date(rowData.readAt)) : "";
  }
  return seedValue(field, rowData);
};

// A single trial-run grid cell that edits read / date / serial inline. It holds the
// in-progress value locally and commits on blur: a valid value calls back with the
// patched rowData; an invalid one surfaces an error and does not commit.
const InlineEditCell: React.FunctionComponent<InlineEditCellProps> = ({
  field,
  rowData,
  editable,
  onCommit,
}) => {
  const { t } = useTranslation();
  const seed = seedValue(field, rowData);
  const [value, setValue] = React.useState(seed);
  const [error, setError] = React.useState<string | null>(null);
  const focusedRef = React.useRef(false);

  // Re-seed from upstream only when not actively editing, so a committed edit (or a
  // future cascade fill) is reflected without clobbering what the operator is typing.
  React.useEffect(() => {
    if (!focusedRef.current) {
      setValue(seed);
      setError(null);
    }
  }, [seed]);

  if (!editable) {
    return <span>{displayText(field, rowData)}</span>;
  }

  const handleBlur = () => {
    focusedRef.current = false;
    const result = commitCell({ field, rawValue: value, rowData });
    if (!result.ok) {
      setError(result.error);
      return;
    }
    setError(null);
    // Commit only on a real change, so a focus/blur with no edit doesn't fire a
    // redundant persist or trial-run re-check.
    if (result.rowData[field] !== rowData[field]) {
      onCommit(result.rowData);
    }
  };

  return (
    <div>
      <input
        type={inputType[field]}
        step={field === "reading" ? "any" : undefined}
        value={value}
        data-inline-field={field}
        aria-label={t(labelKey[field]) || undefined}
        aria-invalid={error ? true : undefined}
        onFocus={() => {
          focusedRef.current = true;
        }}
        onChange={e => setValue(e.target.value)}
        onBlur={handleBlur}
        className={classNames(
          "w-full min-w-[5rem] rounded border bg-transparent px-1 py-0.5 hover:border-gray-300 focus:bg-white focus:outline-blue-500",
          error ? "border-red-400" : "border-transparent",
        )}
      />
      {error ? <ErrorMessage>{t(error)}</ErrorMessage> : null}
    </div>
  );
};

export default InlineEditCell;
